import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, RefreshCw, FileImage, Mail, Phone, User } from 'lucide-react';
import { flyerLeadsApi } from '@/lib/api';
import type { ProgramFlyerLead } from '@/types/program-flyer-lead';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

const HEAR_ABOUT_LABELS: Record<string, string> = {
  google: 'Google search',
  facebook_instagram: 'Facebook / Instagram',
  friend_family: 'Friend or family',
  employer: 'Employer / coworker',
  flyer_print: 'Flyer / print ad',
  school_counselor: 'School / counselor',
  other: 'Other',
  existing_student: 'Logged-in student',
};

interface FlyerLeadDetailProps {
  leadId: string;
}

export const FlyerLeadDetail: React.FC<FlyerLeadDetailProps> = ({ leadId }) => {
  const [lead, setLead] = useState<ProgramFlyerLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const res = await flyerLeadsApi.get(leadId);
      if (res.statusCode !== 200) {
        throw new Error((res as { message?: string }).message || 'Failed to load flyer lead');
      }
      setLead(res.data.lead as ProgramFlyerLead);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !lead) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-muted-foreground mb-4">{error || 'Flyer lead not found'}</p>
        <Button onClick={() => void load()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </Card>
    );
  }

  const hearAboutLabel =
    lead.hearAboutUs === 'other' && lead.hearAboutUsOther
      ? `Other: ${lead.hearAboutUsOther}`
      : HEAR_ABOUT_LABELS[lead.hearAboutUs] || lead.hearAboutUs;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileImage className="h-5 w-5" />
              {lead.leadId}
            </CardTitle>
            <Badge variant="secondary" className="capitalize">
              {lead.flyerTheme} theme
            </Badge>
            <Badge variant="outline">{lead.downloadCount} download{lead.downloadCount === 1 ? '' : 's'}</Badge>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Contact</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {lead.fullName}
                </div>
                <div className="flex items-center gap-2 break-all">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  {lead.email}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {lead.phone}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-1">How they heard about us</h3>
              <p className="text-sm">{hearAboutLabel}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-1">Program</h3>
              <p className="font-medium">{lead.programTitle}</p>
              <p className="text-sm text-muted-foreground capitalize mt-1">
                Mode: {lead.programType.replace(/-/g, ' ')}
              </p>
              {lead.programSlug ? (
                <p className="text-xs text-muted-foreground mt-1 font-mono">/{lead.programSlug}</p>
              ) : null}
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>First captured: {formatAdminBusinessEt(lead.createdAt)}</p>
              <p>
                Last download:{' '}
                {lead.lastDownloadedAt
                  ? formatAdminBusinessEt(lead.lastDownloadedAt)
                  : 'Not downloaded yet'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {lead.downloads?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Download history</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {[...lead.downloads].reverse().map((d, idx) => (
                <li
                  key={`${d.downloadedAt}-${idx}`}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2 last:border-0"
                >
                  <span className="text-muted-foreground">{formatAdminBusinessEt(d.downloadedAt)}</span>
                  <span className="capitalize">{d.programType.replace(/-/g, ' ')}</span>
                  <Badge variant="outline" className="capitalize">
                    {d.flyerTheme}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
